import React, { useState, useEffect } from 'react';
import JobListingForm from '../components/JobListingForm';
import API_BASE from '../utils/apiBase';
import { getLocalJobs, saveLocalJob, deleteLocalJob } from '../utils/jobStorage';

const getJobFileUrl = (job) => {
  const url = job.details_file_url || job.details_file;
  if (!url) return null;
  if (url.startsWith('data:') || url.startsWith('blob:') || url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  const cleanPath = url.startsWith('/') ? url : `/${url}`;
  const BASE_URL = (process.env.REACT_APP_API_BASE || "https://palegreen-snake-884644.hostingersite.com/backend").replace(/\/api$/, "");
  return `${BASE_URL}${cleanPath}`;
};

const handleJobFileDownload = (job, e) => {
  const url = getJobFileUrl(job);
  const fileName = job.details_file_name || `${job.title ? job.title.replace(/\s+/g, '_') : 'Job'}_Details.pdf`;
  if (!url) return;
  if (url.startsWith('data:')) {
    e.preventDefault();
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const AdminJobForm = () => {
  const [refresh, setRefresh] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/api/jobs.php`)
      .then(async (res) => {
        if (!res.ok) throw new Error("HTTP error");
        const text = await res.text();
        return JSON.parse(text);
      })
      .then((data) => {
        if (data && Array.isArray(data.jobs) && data.jobs.length > 0) {
          setJobs(data.jobs);
        } else {
          setJobs(getLocalJobs());
        }
        setLoading(false);
      })
      .catch(() => {
        setJobs(getLocalJobs());
        setLoading(false);
      });
  }, [refresh]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this job?')) return;
    setStatus('Deleting...');
    try {
      await fetch(`${API_BASE}/api/delete_job.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: `id=${id}`
      });
    } catch {
      // Ignore network errors on delete fallback
    }
    const updated = deleteLocalJob(id);
    setJobs(updated);
    setRefresh(r => !r);
    setStatus('');
  };

  const handleEdit = (job) => {
    setEditId(job.id);
    setEditForm({ ...job });
  };

  const handleEditChange = e => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async e => {
    e.preventDefault();
    setStatus('Saving...');
    try {
      await fetch(`${API_BASE}/api/add_job.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...editForm, id: editId })
      });
    } catch {
      // Ignore network errors on edit submit fallback
    }
    saveLocalJob({ ...editForm, id: editId });
    setEditId(null);
    setEditForm(null);
    setRefresh(r => !r);
    setStatus('');
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" style={{ marginTop: "90px" }}>
      <JobListingForm onJobAdded={() => setRefresh(r => !r)} />
      <div className="mt-12">
        <h2 className="text-2xl font-bold mb-6 text-white">Posted Jobs</h2>
        {loading ? (
          <div className="text-blue-400">Loading...</div>
        ) : jobs.length === 0 ? (
          <div className="text-gray-400">No jobs posted yet.</div>
        ) : (
          <div className="overflow-x-auto pb-4">
            <div className="min-w-[760px] space-y-3">
              {jobs.length > 0 && (
                <div className="hidden md:flex bg-slate-950 rounded-t-xl px-4 py-3 font-semibold text-gray-200 text-sm border border-white/10 gap-3">
                  <div className="w-32 shrink-0">Title</div>
                  <div className="w-36 shrink-0">Qualification</div>
                  <div className="w-28 shrink-0">Experience</div>
                  <div className="w-16 shrink-0 text-center">Positions</div>
                  <div className="flex-1 min-w-0">Description</div>
                  <div className="w-28 shrink-0 text-center">Document</div>
                  <div className="w-28 shrink-0 text-center">Actions</div>
                </div>
              )}
              <div className="space-y-3">
                {jobs.map(job => {
                  const docUrl = getJobFileUrl(job);
                  const docName = job.details_file_name || `${job.title ? job.title.replace(/\s+/g, '_') : 'Job'}_Details.pdf`;
                  return (
                    <div key={job.id} className="bg-slate-900 rounded-xl p-4 border border-white/10 shadow-md">
                      {editId === job.id ? (
                        <div className="w-full">
                          <form onSubmit={handleEditSubmit} className="flex flex-col md:flex-row md:items-center gap-3">
                            <input name="title" value={editForm.title} onChange={handleEditChange} placeholder="Title" className="px-3 py-1.5 border border-slate-700 bg-slate-950 text-white rounded text-sm w-32 shrink-0" />
                            <input name="qualification" value={editForm.qualification} onChange={handleEditChange} placeholder="Qualification" className="px-3 py-1.5 border border-slate-700 bg-slate-950 text-white rounded text-sm w-36 shrink-0" />
                            <input name="experience" value={editForm.experience} onChange={handleEditChange} placeholder="Experience" className="px-3 py-1.5 border border-slate-700 bg-slate-950 text-white rounded text-sm w-28 shrink-0" />
                            <input name="positions" type="number" min="1" value={editForm.positions} onChange={handleEditChange} placeholder="Positions" className="px-3 py-1.5 border border-slate-700 bg-slate-950 text-white rounded text-sm w-16 shrink-0" />
                            <textarea name="description" value={editForm.description} onChange={handleEditChange} placeholder="Description" className="px-3 py-1.5 border border-slate-700 bg-slate-950 text-white rounded text-sm flex-1 min-w-0" />
                            <div className="flex gap-2 shrink-0">
                              <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-medium">Save</button>
                              <button type="button" onClick={() => setEditId(null)} className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1.5 rounded text-sm">Cancel</button>
                            </div>
                          </form>
                        </div>
                      ) : (
                        <div className="flex flex-col md:flex-row md:items-center gap-3">
                          <div className="w-32 shrink-0 font-semibold text-sky-400 text-base md:text-sm truncate" title={job.title}>{job.title}</div>
                          <div className="w-36 shrink-0 text-xs text-gray-300 truncate" title={job.qualification}>{job.qualification}</div>
                          <div className="w-28 shrink-0 text-xs text-gray-300 truncate" title={job.experience}>{job.experience}</div>
                          <div className="w-16 shrink-0 text-xs text-gray-300 md:text-center">{job.positions}</div>
                          <div className="flex-1 min-w-0 text-xs text-gray-300 truncate" title={job.description}>{job.description}</div>
                          <div className="w-28 shrink-0 text-xs text-center">
                            {docUrl ? (
                              <a
                                href={docUrl}
                                download={docName}
                                onClick={(e) => handleJobFileDownload(job, e)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-400 hover:underline inline-flex items-center gap-1 font-medium"
                                title={docName}
                              >
                                📄 Details
                              </a>
                            ) : (
                              <span className="text-gray-500 italic">None</span>
                            )}
                          </div>
                          <div className="flex gap-2 mt-2 md:mt-0 w-28 shrink-0 justify-center">
                            <button onClick={() => handleEdit(job)} className="bg-amber-600 hover:bg-amber-700 text-white px-3 py-1 rounded text-xs font-medium">Edit</button>
                            <button onClick={() => handleDelete(job.id)} className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs font-medium">Delete</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {status && <div className="text-center text-sky-400 mt-3 font-medium text-sm">{status}</div>}
      </div>
    </div>
  );
};

export default AdminJobForm;
